import { useState, useEffect, useMemo } from 'react';
import { cn, timeAgo, formatDate } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import type { AuditLogEntry } from '@/types';

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'auth', label: 'Authentication' },
  { value: 'device', label: 'Device' },
  { value: 'session', label: 'Session' },
  { value: 'script', label: 'Script' },
  { value: 'file', label: 'File' },
  { value: 'patch', label: 'Patch' },
  { value: 'user', label: 'User' },
  { value: 'config', label: 'Configuration' },
  { value: 'alert', label: 'Alert' },
];

const RESULTS = [
  { value: 'all', label: 'All Results' },
  { value: 'success', label: 'Success' },
  { value: 'failure', label: 'Failure' },
  { value: 'denied', label: 'Denied' },
];

const RESULT_STYLES: Record<string, string> = {
  success: 'bg-[var(--status-online)]/10 text-[var(--status-online)] border-[var(--status-online)]/20',
  failure: 'bg-[var(--status-critical)]/10 text-[var(--status-critical)] border-[var(--status-critical)]/20',
  denied: 'bg-[var(--status-warning)]/10 text-[var(--status-warning)] border-[var(--status-warning)]/20',
};

const CATEGORY_STYLES: Record<string, string> = {
  auth: 'bg-[var(--accent)]/10 text-[var(--accent)]',
  device: 'bg-[var(--status-info)]/10 text-[var(--status-info)]',
  session: 'bg-[var(--status-online)]/10 text-[var(--status-online)]',
  script: 'bg-[var(--accent)]/10 text-[var(--accent)]',
  file: 'bg-[var(--status-warning)]/10 text-[var(--status-warning)]',
  patch: 'bg-[var(--accent)]/10 text-[var(--accent)]',
  user: 'bg-[var(--text-muted)]/10 text-[var(--text-muted)]',
  config: 'bg-[var(--text-muted)]/10 text-[var(--text-muted)]',
  alert: 'bg-[var(--status-critical)]/10 text-[var(--status-critical)]',
};

export function AuditLog() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [result, setResult] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    mockApi.getAuditLog({ category, result, search }).then((data) => {
      setLogs(data);
      setLoading(false);
    });
  }, [category, result, search]);

  const filtered = useMemo(() => {
    return logs;
  }, [logs]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleExport = () => {
    const event = new CustomEvent('toast', { detail: { message: `Exported ${filtered.length} audit log entries`, type: 'success' } });
    window.dispatchEvent(event);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[var(--text-muted)] text-sm">Loading audit log…</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Audit Log</h1>
          <p className="text-sm text-[var(--text-muted)] mt-0.5">{filtered.length} entries</p>
        </div>
        <Button variant="outline" onClick={handleExport}>Export</Button>
      </div>

      {/* Append-only notice */}
      <div className="flex items-center gap-2 bg-[var(--panel)] border border-[var(--border)] rounded-lg px-4 py-3">
        <svg className="w-4 h-4 text-[var(--status-warning)] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-sm text-[var(--text-secondary)]">
          Audit logs are <span className="text-[var(--text-primary)] font-medium">append-only</span> and cannot be modified by any user, including administrators.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search action, user, hostname…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="flex-1 min-w-[200px] bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)]"
        />
        <select
          value={category}
          onChange={(e) => { setCategory(e.target.value); setPage(1); }}
          className="bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
        >
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select
          value={result}
          onChange={(e) => { setResult(e.target.value); setPage(1); }}
          className="bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
        >
          {RESULTS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-[var(--panel)] border border-[var(--border)] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border)] text-xs text-[var(--text-muted)] uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-medium">Timestamp</th>
              <th className="text-left px-4 py-3 font-medium">User</th>
              <th className="text-left px-4 py-3 font-medium">Action</th>
              <th className="text-left px-4 py-3 font-medium">Category</th>
              <th className="text-left px-4 py-3 font-medium">Result</th>
              <th className="text-left px-4 py-3 font-medium">Source IP</th>
              <th className="text-left px-4 py-3 font-medium">Device</th>
              <th className="text-left px-4 py-3 font-medium">Correlation ID</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((log) => (
              <>
                <tr
                  key={log.id}
                  className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover)] cursor-pointer"
                  onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                >
                  <td className="px-4 py-3 text-xs text-[var(--text-muted)] whitespace-nowrap">{formatDate(log.timestamp)}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-[var(--text-primary)]">{log.userName}</div>
                    <div className="text-xs text-[var(--text-muted)]">{log.userRole}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">{log.action}</td>
                  <td className="px-4 py-3">
                    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', CATEGORY_STYLES[log.category])}>
                      {log.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border', RESULT_STYLES[log.result])}>
                      {log.result}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)] font-mono">{log.sourceIp}</td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">{log.hostname || '—'}</td>
                  <td className="px-4 py-3 text-xs text-[var(--text-muted)] font-mono truncate max-w-[120px]">{log.correlationId.slice(0, 8)}…</td>
                </tr>
                {expandedRow === log.id && (
                  <tr className="bg-[var(--elevated)]">
                    <td colSpan={8} className="px-4 py-4">
                      <div className="grid grid-cols-2 gap-4 max-w-2xl">
                        <DetailRow label="Correlation ID" value={log.correlationId} mono />
                        <DetailRow label="User Agent" value={log.userAgent} />
                        <DetailRow label="Source IP" value={log.sourceIp} mono />
                        <DetailRow label="Device ID" value={log.deviceId || 'N/A'} mono />
                        {Object.keys(log.metadata).length > 0 && (
                          <div className="col-span-2">
                            <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-1.5">Metadata</p>
                            <pre className="bg-[var(--bg)] rounded p-3 text-xs text-[var(--text-secondary)] font-mono overflow-x-auto">
                              {JSON.stringify(log.metadata, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
        {paged.length === 0 && (
          <div className="text-center py-12">
            <p className="text-sm text-[var(--text-muted)]">No audit log entries found matching your filters.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[var(--text-muted)]">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            {Array.from({ length: Math.min(totalPages, 10) }).map((_, i) => {
              const p = i + 1;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={cn(
                    'px-3 py-1.5 text-sm rounded-md transition-colors',
                    p === page ? 'bg-[var(--accent)] text-white' : 'text-[var(--text-muted)] hover:bg-[var(--hover)]'
                  )}
                >
                  {p}
                </button>
              );
            })}
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-0.5">{label}</p>
      <p className={cn('text-sm text-[var(--text-secondary)]', mono && 'font-mono')}>{value}</p>
    </div>
  );
}
